import React, { useEffect, useState, useCallback } from 'react';
import { HiOutlineUpload, HiOutlineTrash, HiOutlinePencil, HiOutlineCheck } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import { MONTHS, periodLabel, fmtMoney, fmtPct } from '../utils/format';
import PnLEditor from './PnLEditor';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 3, CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const emptyDraft = () => ({
    businessUnit: '',
    month: new Date().getMonth() + 1,
    year: CURRENT_YEAR,
    periodLabel: '',
    directIncome: [],
    directExpenses: [],
    indirectExpenses: [],
    indirectIncome: [],
    totalDirectIncome: { amount: 0, percent: 100 },
    totalDirectExpenses: { amount: 0, percent: 0 },
    grossProfitLoss: { amount: 0, percent: 0 },
    totalIndirectExpenses: { amount: 0, percent: 0 },
    totalExpense: { amount: 0, percent: 0 },
    netOperatingProfitLoss: { amount: 0, percent: 0 },
    totalIndirectIncome: { amount: 0, percent: 0 },
    netProfitLoss: { amount: 0, percent: 0 },
    notes: ''
});

const STATUS_BADGES = {
    PENDING:  { label: 'Pending',  cls: 'fr-badge-pending'  },
    APPROVED: { label: 'Approved', cls: 'fr-badge-approved' },
    REJECTED: { label: 'Rejected', cls: 'fr-badge-rejected' }
};

const UploadTab = ({ canCreate, canApprove, canEdit, canDelete }) => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [reports, setReports] = useState([]);
    const [filters, setFilters] = useState({ businessUnit: '', year: '', approvalStatus: '' });
    const [loadingList, setLoadingList] = useState(false);

    const [draft, setDraft] = useState(emptyDraft());
    const [editingId, setEditingId] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [parseError, setParseError] = useState('');

    const fetchBusinessUnits = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/financial-reports/business-units');
            if (data.success) setBusinessUnits(data.data || []);
        } catch (err) {
            console.error('Failed to load business units', err);
        }
    }, []);

    const fetchReports = useCallback(async () => {
        setLoadingList(true);
        try {
            const params = {};
            if (filters.businessUnit) params.businessUnit = filters.businessUnit;
            if (filters.year) params.year = filters.year;
            if (filters.approvalStatus) params.approvalStatus = filters.approvalStatus;

            const { data } = await axiosInstance.get('/api/financial-reports', { params });
            if (data.success) setReports(data.data || []);
        } catch (err) {
            console.error('Failed to load reports', err);
        } finally {
            setLoadingList(false);
        }
    }, [filters]);

    useEffect(() => { fetchBusinessUnits(); }, [fetchBusinessUnits]);
    useEffect(() => { fetchReports(); }, [fetchReports]);

    // === PDF parse ===
    const handlePdfSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            setParseError('Only PDF files are accepted');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setParseError('File too large (max 10MB)');
            return;
        }
        setParseError('');
        setParsing(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await axiosInstance.post(
                '/api/financial-reports/parse-pdf',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (data.success) {
                const parsed = data.data;
                setDraft(prev => ({
                    ...prev,
                    businessUnit: parsed.businessUnit && businessUnits.includes(parsed.businessUnit)
                        ? parsed.businessUnit
                        : (businessUnits.includes(parsed.businessUnit) ? parsed.businessUnit : prev.businessUnit),
                    month: parsed.month || prev.month,
                    year: parsed.year || prev.year,
                    periodLabel: parsed.periodLabel || '',
                    directIncome: parsed.directIncome || [],
                    directExpenses: parsed.directExpenses || [],
                    indirectExpenses: parsed.indirectExpenses || [],
                    indirectIncome: parsed.indirectIncome || [],
                    totalDirectIncome: parsed.totalDirectIncome || { amount: 0, percent: 0 },
                    totalDirectExpenses: parsed.totalDirectExpenses || { amount: 0, percent: 0 },
                    grossProfitLoss: parsed.grossProfitLoss || { amount: 0, percent: 0 },
                    totalIndirectExpenses: parsed.totalIndirectExpenses || { amount: 0, percent: 0 },
                    totalExpense: parsed.totalExpense || { amount: 0, percent: 0 },
                    netOperatingProfitLoss: parsed.netOperatingProfitLoss || { amount: 0, percent: 0 },
                    totalIndirectIncome: parsed.totalIndirectIncome || { amount: 0, percent: 0 },
                    netProfitLoss: parsed.netProfitLoss || { amount: 0, percent: 0 }
                }));
            }
        } catch (err) {
            console.error('Parse error', err);
            setParseError(err.response?.data?.message || 'Failed to parse PDF');
        } finally {
            setParsing(false);
            e.target.value = '';
        }
    };

    // === Save / Approve ===
    const saveDraft = async (alsoApprove = false) => {
        if (!draft.businessUnit) {
            alert('Pick a Business Unit'); return;
        }
        if (!draft.month || !draft.year) {
            alert('Pick a month and year'); return;
        }
        setSaving(true);
        try {
            let saved;
            if (editingId) {
                const { data } = await axiosInstance.put(`/api/financial-reports/${editingId}`, draft);
                if (!data.success) throw new Error(data.message);
                saved = data.data;
            } else {
                const { data } = await axiosInstance.post('/api/financial-reports', draft);
                if (!data.success) throw new Error(data.message);
                saved = data.data;
            }
            if (alsoApprove && saved?._id && canApprove) {
                await axiosInstance.patch(`/api/financial-reports/${saved._id}/approve`, {
                    approvalStatus: 'APPROVED'
                });
            }
            setDraft(emptyDraft());
            setEditingId(null);
            await fetchReports();
            alert(alsoApprove ? 'Report saved & approved' : 'Report saved');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (r) => {
        setEditingId(r._id);
        setDraft({
            businessUnit: r.businessUnit,
            month: r.month,
            year: r.year,
            periodLabel: r.periodLabel || '',
            directIncome: r.directIncome || [],
            directExpenses: r.directExpenses || [],
            indirectExpenses: r.indirectExpenses || [],
            indirectIncome: r.indirectIncome || [],
            totalDirectIncome: r.totalDirectIncome || { amount: 0, percent: 0 },
            totalDirectExpenses: r.totalDirectExpenses || { amount: 0, percent: 0 },
            grossProfitLoss: r.grossProfitLoss || { amount: 0, percent: 0 },
            totalIndirectExpenses: r.totalIndirectExpenses || { amount: 0, percent: 0 },
            totalExpense: r.totalExpense || { amount: 0, percent: 0 },
            netOperatingProfitLoss: r.netOperatingProfitLoss || { amount: 0, percent: 0 },
            totalIndirectIncome: r.totalIndirectIncome || { amount: 0, percent: 0 },
            netProfitLoss: r.netProfitLoss || { amount: 0, percent: 0 },
            notes: r.notes || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (r) => {
        if (!window.confirm(`Delete the ${periodLabel(r.month, r.year)} report for ${r.businessUnit}?`)) return;
        try {
            await axiosInstance.delete(`/api/financial-reports/${r._id}`);
            await fetchReports();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete');
        }
    };

    const handleApproveToggle = async (r) => {
        try {
            const next = r.approvalStatus === 'APPROVED' ? 'PENDING' : 'APPROVED';
            await axiosInstance.patch(`/api/financial-reports/${r._id}/approve`, { approvalStatus: next });
            await fetchReports();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update approval');
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft(emptyDraft());
    };

    return (
        <div className="fr-upload-tab">
            {/* === Draft editor === */}
            <section className="fr-card fr-editor-card">
                <div className="fr-card-header">
                    <h2>{editingId ? 'Edit Report' : 'New Report'}</h2>
                    <p>Drop a Tally P&L PDF to auto-fill, then review and save.</p>
                </div>

                <div className="fr-editor-meta">
                    <div className="fr-form-group">
                        <label>Business Unit *</label>
                        <select
                            value={draft.businessUnit}
                            onChange={e => setDraft({ ...draft, businessUnit: e.target.value })}
                        >
                            <option value="">— Select —</option>
                            {businessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                        </select>
                    </div>
                    <div className="fr-form-group">
                        <label>Month *</label>
                        <select
                            value={draft.month}
                            onChange={e => setDraft({ ...draft, month: Number(e.target.value) })}
                        >
                            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                        </select>
                    </div>
                    <div className="fr-form-group">
                        <label>Year *</label>
                        <select
                            value={draft.year}
                            onChange={e => setDraft({ ...draft, year: Number(e.target.value) })}
                        >
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="fr-form-group fr-pdf-group">
                        <label>Auto-fill from PDF</label>
                        <label className="fr-pdf-btn">
                            <HiOutlineUpload size={16} />
                            {parsing ? 'Parsing…' : 'Choose PDF'}
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handlePdfSelect}
                                disabled={parsing}
                                style={{ display: 'none' }}
                            />
                        </label>
                        {parseError && <div className="fr-parse-error">{parseError}</div>}
                    </div>
                </div>

                <PnLEditor draft={draft} onChange={setDraft} />

                <div className="fr-form-group" style={{ marginTop: 16 }}>
                    <label>Notes (optional)</label>
                    <textarea
                        value={draft.notes}
                        onChange={e => setDraft({ ...draft, notes: e.target.value })}
                        rows={2}
                        placeholder="Add any management note for this period…"
                    />
                </div>

                <div className="fr-editor-actions">
                    {editingId && (
                        <button className="fr-btn fr-btn-secondary" onClick={cancelEdit} disabled={saving}>
                            Cancel
                        </button>
                    )}
                    {canCreate && (
                        <button
                            className="fr-btn fr-btn-primary"
                            onClick={() => saveDraft(false)}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Save as Pending')}
                        </button>
                    )}
                    {canApprove && (
                        <button
                            className="fr-btn fr-btn-success"
                            onClick={() => saveDraft(true)}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save & Approve'}
                        </button>
                    )}
                </div>
            </section>

            {/* === Reports list === */}
            <section className="fr-card">
                <div className="fr-card-header">
                    <h2>Uploaded Reports</h2>
                    <p>All P&L reports stored in the system.</p>
                </div>

                <div className="fr-list-filters">
                    <select
                        value={filters.businessUnit}
                        onChange={e => setFilters({ ...filters, businessUnit: e.target.value })}
                    >
                        <option value="">All business units</option>
                        {businessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                    </select>
                    <select
                        value={filters.year}
                        onChange={e => setFilters({ ...filters, year: e.target.value })}
                    >
                        <option value="">All years</option>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select
                        value={filters.approvalStatus}
                        onChange={e => setFilters({ ...filters, approvalStatus: e.target.value })}
                    >
                        <option value="">All statuses</option>
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                    </select>
                </div>

                <div className="fr-table-wrap">
                    <table className="fr-table">
                        <thead>
                            <tr>
                                <th>Period</th>
                                <th>Business Unit</th>
                                <th className="fr-num">Sales</th>
                                <th className="fr-num">Gross %</th>
                                <th className="fr-num">Net Profit</th>
                                <th className="fr-num">Net %</th>
                                <th>Status</th>
                                <th>Uploaded By</th>
                                <th className="fr-actions-col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingList ? (
                                <tr><td colSpan={9} className="fr-empty">Loading…</td></tr>
                            ) : reports.length === 0 ? (
                                <tr><td colSpan={9} className="fr-empty">No reports yet. Upload your first one above.</td></tr>
                            ) : reports.map(r => {
                                const badge = STATUS_BADGES[r.approvalStatus] || STATUS_BADGES.PENDING;
                                return (
                                    <tr key={r._id}>
                                        <td><strong>{periodLabel(r.month, r.year, false)}</strong></td>
                                        <td>{r.businessUnit}</td>
                                        <td className="fr-num">{fmtMoney(r.totalDirectIncome?.amount)}</td>
                                        <td className="fr-num">{fmtPct(r.grossProfitLoss?.percent)}</td>
                                        <td className="fr-num">{fmtMoney(r.netProfitLoss?.amount)}</td>
                                        <td className="fr-num">{fmtPct(r.netProfitLoss?.percent)}</td>
                                        <td><span className={`fr-badge ${badge.cls}`}>{badge.label}</span></td>
                                        <td>{r.uploadedByName || '—'}</td>
                                        <td className="fr-actions">
                                            {canEdit && (
                                                <button className="fr-icon-btn" title="Edit" onClick={() => handleEdit(r)}>
                                                    <HiOutlinePencil size={16} />
                                                </button>
                                            )}
                                            {canApprove && (
                                                <button
                                                    className="fr-icon-btn"
                                                    title={r.approvalStatus === 'APPROVED' ? 'Mark pending' : 'Approve'}
                                                    onClick={() => handleApproveToggle(r)}
                                                >
                                                    <HiOutlineCheck size={16} />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button className="fr-icon-btn fr-icon-danger" title="Delete" onClick={() => handleDelete(r)}>
                                                    <HiOutlineTrash size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default UploadTab;
